import { useState, useEffect, useCallback, useRef } from 'react';

export type ConnectionStatus = 'online' | 'mockup' | 'disconnected';

interface ConnectionState {
    status: ConnectionStatus;
    label: string;
    latency?: number;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3006/api';
const HEALTH_CHECK_INTERVAL = 30_000; // 30s
const HEALTH_CHECK_TIMEOUT = 5_000;   // 5s timeout

/**
 * Hook kiểm tra trạng thái kết nối API
 * - 🟢 online: API phản hồi thành công  
 * - 🟡 mockup: Đang dùng mock data (không có API)
 * - 🔴 disconnected: Mất kết nối
 */
export function useConnectionStatus(): ConnectionState {
    const [state, setState] = useState<ConnectionState>({
        status: 'disconnected',
        label: 'Offline',
    });
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const checkHealth = useCallback(async () => {
        const start = Date.now();
        try {
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), HEALTH_CHECK_TIMEOUT);

            const res = await fetch(`${API_BASE_URL}/health`, {
                signal: controller.signal,
            });
            clearTimeout(timeout);

            const latency = Date.now() - start;

            if (res.ok) {
                const data = await res.json();
                if (data?.success || data?.data?.message) {
                    setState({ status: 'online', label: 'Online', latency });
                } else {
                    setState({ status: 'mockup', label: 'Demo', latency });
                }
            } else {
                setState({ status: 'disconnected', label: 'Offline', latency });
            }
        } catch {
            // Check if we have mock data sources configured
            const hasMockFlag = import.meta.env.VITE_USE_MOCK === 'true';
            if (hasMockFlag) {
                setState({ status: 'mockup', label: 'Demo' });
            } else {
                setState({ status: 'disconnected', label: 'Offline' });
            }
        }
    }, []);

    useEffect(() => {
        checkHealth();
        intervalRef.current = setInterval(checkHealth, HEALTH_CHECK_INTERVAL);
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [checkHealth]);

    return state;
}
